/**
 * Bandeau « accès exceptionnel de la plateforme en cours » (lot PL ; SRF-PLT-009, SRE-2-02 ;
 * planche PltAcces.dc.html, état « active (fin prévue) » : visible de TOUS dans l'organisation).
 *
 * Monté une fois par la coquille. Il lit les sessions ACTIVES qui visent l'organisation de
 * l'utilisateur (`GET /organisations/{id}/exceptional-access`, tout membre) ; la base décide de
 * ce qui est actif. Silencieux en cas d'erreur : le bandeau informe, il ne bloque rien, et la
 * notification des propriétaires reste la voie garantie.
 */
import type { JSX } from 'react'
import { useQuery } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'

import { plateformeApi } from '@/api/plateformeApi'
import { useT } from '@/i18n/language'
import { formaterInstant } from '@/lib/arretIA'

export function BandeauAccesExceptionnel({ organisation }: { organisation: string | undefined }): JSX.Element | null {
  const { t } = useT()
  const actifs = useQuery({
    queryKey: ['organisation', organisation, 'acces-exceptionnels'],
    queryFn: () => plateformeApi.accesActifsOrganisation(organisation as string),
    enabled: Boolean(organisation),
    refetchInterval: 60_000,
    retry: false,
  })
  const sessions = actifs.data?.active ?? []
  const premiere = sessions[0]
  if (!premiere) return null
  const fin = sessions.map((s) => s.expires_at).sort().at(-1)
  return (
    <section aria-label={t('accesBandeau.region', 'Accès exceptionnel de la plateforme')} data-testid="bandeau-acces-exceptionnel"
             className="border-b border-[var(--color-warning)] bg-[color-mix(in_srgb,var(--color-warning)_12%,transparent)] px-3 py-2 text-sm">
      <p className="flex flex-wrap items-baseline gap-x-1.5" aria-live="polite">
        <KeyRound className="size-4 shrink-0 self-center" aria-hidden="true" />
        <strong className="font-semibold">
          {t('accesBandeau.titre', 'Accès exceptionnel de la plateforme en cours jusqu’à {{fin}}', { fin: formaterInstant(fin) ?? '—' })}
        </strong>
        <span>— {t('accesBandeau.detail', 'en lecture seule, par {{qui}} : « {{motif}} ». Chaque consultation est écrite à votre audit.', {
          qui: premiere.actor_id, motif: premiere.reason,
        })}</span>
      </p>
    </section>
  )
}
